import type { ErrorInfo, ReactNode } from 'react';
import React from 'react';
import supportedBrowsers from '../app/supportedBrowsers';
import Logger from '../utils/Logger';

// https://github.com/facebook/react/issues/11538
const checkIfIsCausedByTranslation = (error: Error) => {
  const stack = error.stack || '';
  const isReactDOMMutationError =
    stack.includes('removeChild') || stack.includes('insertBefore');
  if (!isReactDOMMutationError) return false;

  const isPageTranslated = !!document.querySelector(
    [
      // Google Translate
      '.translated-ltr',
      '.translated-rtl',
      // Edge built-in translation
      '[_msttexthash]',
    ].join(),
  );
  return isPageTranslated;
};

export type ErrorBoundaryType =
  | 'translation'
  | 'unsupportedBrowser'
  | 'unknown';

export interface ErrorBoundaryMetadata {
  type: ErrorBoundaryType;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  name: string;
  nonFatal?: boolean;
  renderFallback?: (metadata: ErrorBoundaryMetadata) => ReactNode;
}

type FatalErrorBoundaryState = {
  errorState: ErrorBoundaryType | 'no-error';
};

function getType(error: Error): ErrorBoundaryType {
  if (checkIfIsCausedByTranslation(error)) return 'translation';
  if (!supportedBrowsers.test(navigator.userAgent)) return 'unsupportedBrowser';
  return 'unknown';
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  FatalErrorBoundaryState
> {
  static getDerivedStateFromError(error: Error): FatalErrorBoundaryState {
    return {
      errorState: getType(error),
    };
  }

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { errorState: 'no-error' };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const isSupportedBrowser = supportedBrowsers.test(navigator.userAgent);
    const isFatal =
      !checkIfIsCausedByTranslation(error) &&
      isSupportedBrowser &&
      !this.props.nonFatal;
    const level = isFatal ? 'fatal' : 'error';

    Logger[level](error, errorInfo, {
      tags: { errorBoundary: this.props.name },
    });
  }

  render() {
    if (this.state.errorState === 'no-error') {
      return this.props.children;
    }

    return (
      this.props.renderFallback?.({
        type: this.state.errorState,
      }) ?? null
    );
  }
}

export default ErrorBoundary;
